import type { AxiosInstance } from 'axios';

export type NoteType = 'REFUND' | 'WARRANTY' | 'SHIPPING' | 'RETURN' | 'PAYMENT' | 'GENERAL';
export type NoteAuthorKind = 'IN_HOUSE' | 'SELLER';

export interface Note {
  id: string;
  type: NoteType;
  description: string;
  authorKind: NoteAuthorKind;
  authorId: string | null;
  vendorId: string | null;
  sellerCanAccess: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NoteSetting {
  sellerCanAddNote: boolean;
  updatedAt: string;
}

const unwrap = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

export function noteEndpoints(http: AxiosInstance) {
  return {
    list: (params?: { authorKind?: NoteAuthorKind; q?: string }) =>
      unwrap<Note[]>(http.get('/admin/notes', { params })),
    create: (body: {
      type: NoteType;
      description: string;
      authorKind?: NoteAuthorKind;
      sellerCanAccess?: boolean;
    }) => http.post<Note>('/admin/notes', body).then((r) => r.data),
    update: (
      noteId: string,
      body: Partial<{ type: NoteType; description: string; sellerCanAccess: boolean }>,
    ) => http.patch<Note>(`/admin/notes/${noteId}`, body).then((r) => r.data),
    remove: (noteId: string) => http.delete(`/admin/notes/${noteId}`).then((r) => r.data),
    getConfig: () => http.get<NoteSetting>('/admin/notes/config').then((r) => r.data),
    /** Seller — whether sellers may add notes right now. */
    getVendorConfig: () => http.get<NoteSetting>('/vendor/notes/config').then((r) => r.data),
    updateConfig: (sellerCanAddNote: boolean) =>
      http.put<NoteSetting>('/admin/notes/config', { sellerCanAddNote }).then((r) => r.data),

    // Vendor variants
    listForVendor: (params?: { q?: string }) =>
      unwrap<Note[]>(http.get('/vendor/notes', { params })),
    createByVendor: (body: { type: NoteType; description: string }) =>
      http.post<Note>('/vendor/notes', body).then((r) => r.data),
  };
}

export type NoteEndpoints = ReturnType<typeof noteEndpoints>;
